<template>
    <div class="ppv2-mx-auto ppv2-p-4">
        <!-- Confirmation Dialog -->
        <ConfirmationDialog
            :is-open="showDeleteConfirm"
            :title="$t('common.confirm_delete')"
            :message="$t('resource_planning.exceptions.confirm_delete')"
            type="danger"
            confirm-button-variant="danger"
            @confirm="confirmDelete"
            @cancel="cancelDelete"
        />

        <!-- Add Exception Panel -->
        <PanelComponent
            v-if="showAddPanel"
            :title="
                mode === 'add'
                    ? $t('resource_planning.exceptions.add_exception')
                    : $t('resource_planning.exceptions.edit_exception')
            "
            :width="'800px'"
            :footer="false"
            @close="closeAddPanel(false)"
            @outside-close="closeAddPanel(false)"
        >
            <ExceptionRangePlanning
                ref="exceptionPlanner"
                @save="handleSaveExceptions"
                class="ppv2-mt-4"
                :mode="mode"
                :selectedException="selectedException"
            />
        </PanelComponent>

        <!-- Header -->
        <div class="ppv2-flex ppv2-flex-wrap ppv2-justify-between ppv2-items-center ppv2-mb-6">
            <div>
                <h1 class="ppv2-text-2xl poppinsBold ppv2-text-gray-900">
                    {{ $t('resource_planning.exceptions.exceptions') }}
                </h1>
                <p class="ppv2-text-sm ppv2-text-gray-600">{{ $t('resource_planning.exceptions.exceptions_description') }}</p>
            </div>
            <button class="add-button" @click="addNewException">
                <div class="button-content">
                    <div class="button-text">
                        {{ $t('resource_planning.exceptions.add_button') }}
                    </div>
                </div>
            </button>
        </div>

        <!-- Exceptions List -->
        <div class="ppv2-space-y-4">
            <div v-for="(exception, index) in exceptions" :key="'exception-' + index" class="schedule-card">
                <div class="ppv2-flex ppv2-justify-between ppv2-items-center">
                    <div class="ppv2-flex ppv2-items-center">
                        <div class="ppv2-mr-4">
                            <div class="ppv2-text-lg poppinsSemiBold">
                                <h3 class="poppinsBold">{{ exception.title }}</h3>
                            </div>
                            <div class="ppv2-text-lg poppinsSemiBold">{{ formatDisplayDate(exception.start) }}</div>
                            <div
                                v-if="exception.end && exception.end !== exception.start"
                                class="ppv2-text-sm ppv2-text-gray-600"
                            >
                                {{ $t('common.to') }} {{ formatDisplayDate(exception.end) }}
                            </div>
                        </div>
                        <div class="ppv2-flex ppv2-items-center ppv2-space-x-3">
                            <div class="status-badge" :style="{ backgroundColor: exception.color ?? '#FF6B35' }">
                                {{ getStatusTitle(exception.status) }}
                            </div>
                            <!-- <div class="establishment-info">
                                {{ $t('resource_planning.exceptions.establishment') }}: {{ exception.establishment_id }}
                            </div> -->
                        </div>
                    </div>
                    <div class="ppv2-flex ppv2-space-x-2">
                        <button @click="editException(exception)" class="ppv2-p-2 ppv2-text-gray-600 hover:ppv2-text-gray-900">
                            <svg class="ppv2-w-5 ppv2-h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                />
                            </svg>
                        </button>
                        <button @click="deleteException(exception)" class="ppv2-p-2 ppv2-text-red-600 hover:ppv2-text-red-900">
                            <svg class="ppv2-w-5 ppv2-h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                            </svg>
                        </button>
                    </div>
                </div>

                <!-- Exception Details -->
                <!-- <div class="ppv2-mt-4 ppv2-pt-4 ppv2-border-t ppv2-border-gray-200">
                    <div class="ppv2-grid ppv2-grid-cols-1 md:ppv2-grid-cols-2" :style="{gap: normalizeSize(16, true)}">
                        <div>
                            <h4 class="poppinsMedium ppv2-mb-2">{{ $t('resource_planning.exceptions.period') }}:</h4>
                            <div class="ppv2-text-sm ppv2-text-gray-600">
                                {{ formatDisplayDate(exception.start) }}
                                <span v-if="exception.end && exception.end !== exception.start">
                                    - {{ formatDisplayDate(exception.end) }}
                                </span>
                            </div>
                        </div>
                        <div>
                            <h4 class="poppinsMedium ppv2-mb-2">{{ $t('resource_planning.exceptions.status') }}:</h4>
                            <div class="ppv2-text-sm ppv2-text-gray-600">
                                {{ getStatusTitle(exception.status) }}
                            </div>
                        </div>
                    </div>
                </div> -->
            </div>
        </div>

        <!-- Empty State -->
        <div v-if="exceptions.length === 0 && !isLoading" class="empty-state">
            <div class="ppv2-text-center ppv2-py-12">
                <svg
                    class="ppv2-mx-auto ppv2-h-12 ppv2-w-12 ppv2-text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                </svg>
                <h3 class="ppv2-mt-2 ppv2-text-sm poppinsMedium">
                    {{ $t('resource_planning.exceptions.add_exception') }}
                </h3>
                <p class="ppv2-mt-1 ppv2-text-sm ppv2-text-gray-500 mb-0">
                    {{ $t('resource_planning.exceptions.no_exceptions_description') }}
                </p>
                <div class="ppv2-mt-6">
                    <button @click="addNewException" class="add-button">
                        <div class="button-content">
                            <div class="button-text">
                                {{ $t('resource_planning.exceptions.add_button') }}
                            </div>
                        </div>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import normalizeSize from '../../../utils/normalizeSize'

import ApiEstablishmentManager from '../../../helpers/ApiEstablishmentManager'
import PanelComponent from '../../Shared/PanelComponent.vue'
import ConfirmationDialog from '../../Shared/ConfirmationDialog.vue'
import ExceptionRangePlanning from './ExceptionRangePlanning.vue'

import { colors } from 'vuetify/lib'

// Status mapping for exceptions with i18n keys
const EXCEPTION_STATUS = {
    0: { key: 'resource_planning.reasons.works', color: '#FF6B35' },
    1: { key: 'resource_planning.reasons.exceptional_closure', color: '#4ECDC4' },
    2: { key: 'resource_planning.reasons.breakdown', color: '#FF4757' },
    3: { key: 'resource_planning.reasons.maintenance', color: '#3742FA' },
    4: { key: 'resource_planning.reasons.team_meeting', color: '#2ED573' },
    5: { key: 'resource_planning.reasons.private_event', color: '#FFA502' },
}

export default {
    name: 'ExceptionScheduling',
    components: {
        PanelComponent,
        ConfirmationDialog,
        ExceptionRangePlanning,
    },
    data() {
        return {
            exceptions: [],
            isLoading: false,
            error: null,
            showAddPanel: false,
            showDeleteConfirm: false,
            mode: 'add',
            selectedException: null,
            exceptionToDelete: null,
            establishmentId: 1,
            dataToSave: null,
        }
    },
    created() {
        this.loadExceptions()
    },
    methods: {
        normalizeSize,
        async loadExceptions() {
            this.isLoading = true
            try {
                const today = new Date()

                // Start of today at 00:00:00
                const start = new Date(today)
                start.setHours(0, 0, 0, 0)

                // End of the same day one year later at 23:59:59
                const end = new Date(today)
                end.setFullYear(today.getFullYear() + 1)
                end.setHours(23, 59, 59, 999)

                // Format function: YYYY-MM-DD HH:mm:ss
                const formatDateTime = (date) => {
                    const pad = (n) => n.toString().padStart(2, '0')
                    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(
                        date.getHours()
                    )}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
                }

                const params = {
                    start: formatDateTime(start), // e.g. "2025-07-03 00:00:00"
                    end: formatDateTime(end), // e.g. "2026-07-03 23:59:59"
                    establishmentId: this.establishmentId,
                }
                const response = await ApiEstablishmentManager.getExceptionSchedules(params)
                this.exceptions = this.prepareExceptions(response.data)
            } catch (error) {
                this.$toast.error(error.response.data.message)
                this.error = this.$t('common.error_loading_data')
            } finally {
                this.isLoading = false
            }
        },
        prepareExceptions(apiData) {
            // Transform API data to match our component's format
            return apiData.map((exception) => ({
                title: exception.label,
                color: exception.color,
                id: exception.id,
                establishment_id: exception.establishment_id,
                start: exception.start,
                end: exception.end,
                status: exception.status || 0,
            }))
        },
        formatDisplayDate(dateString) {
            if (!dateString) return ''
            const options = {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            }
            return new Date(dateString).toLocaleDateString(this.$i18n.locale, options)
        },
        getStatusTitle(status) {
            const statusObj = EXCEPTION_STATUS[status]
            return statusObj ? this.$t(statusObj.key) : this.$t('common.unknown')
        },
        getStatusColor(status) {
            return EXCEPTION_STATUS[status]?.color || '#6B7280'
        },
        addNewException() {
            this.mode = 'add'
            this.selectedException = null
            this.showAddPanel = true
        },
        editException(exception) {
            this.mode = 'edit'
            this.selectedException = { ...exception }
            this.showAddPanel = true
        },
        deleteException(exception) {
            this.exceptionToDelete = exception
            this.showDeleteConfirm = true
        },
        handleDataToSave(data) {
            this.dataToSave = data
        },
        async confirmDelete() {
            if (!this.exceptionToDelete) return

            try {
                await ApiEstablishmentManager.deleteExceptionSchedule(this.establishmentId, this.exceptionToDelete.id)
                this.$toast.success(this.$t('resource_planning.exceptions.delete_success'))

                await this.loadExceptions()
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error deleting exception:', error)
                this.$toast.error(this.$t('common.error_deleting'))
            } finally {
                this.cancelDelete()
            }
        },
        cancelDelete() {
            this.exceptionToDelete = null
            this.showDeleteConfirm = false
        },
        closeAddPanel() {
            this.showAddPanel = false
            this.selectedException = null
        },
        confirmException() {
            if (this.$refs.exceptionPlanner) {
                this.$refs.exceptionPlanner.saveException()
            }
        },
        async handleSaveExceptions(exceptionData) {
            try {
                const dataToSave = ApiEstablishmentManager.setExceptionStructure(this.establishmentId, exceptionData)

                if (this.mode === 'add') {
                    await ApiEstablishmentManager.createExceptionSchedule(this.establishmentId, dataToSave)
                    this.$toast.success(this.$t('resource_planning.exceptions.create_success'))
                } else {
                    await ApiEstablishmentManager.updateExceptionSchedule(this.establishmentId, exceptionData.id, dataToSave)
                    this.$toast.success(this.$t('resource_planning.exceptions.update_success'))
                }

                // Hide message after 5 seconds
                setTimeout(() => {
                    this.showMessage = false
                }, 5000)

                this.closeAddPanel()
                await this.loadExceptions()
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error saving exception:', error)
                this.$toast.error(this.$t('common.error_saving'))
            }
        },
    },
}
</script>

<style scoped>
.header-title {
    @apply ppv2-text-2xl ppv2-font-bold ppv2-text-gray-900;
}

.header-description {
    @apply ppv2-mt-1 ppv2-text-sm ppv2-text-gray-500;
}

.add-button {
    @apply ppv2-bg-transparent ppv2-border-none ppv2-cursor-pointer ppv2-p-0;
    outline: none;
}

.add-button .button-content {
    width: 100%;
    height: 100%;
    padding: 10px;
    border-radius: 36px;
    outline: 2px solid #0f172a;
    outline-offset: -1px;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    gap: 10px;
    background: white;
    transition: all 0.2s ease;
}

.add-button .button-text {
    color: #021832;
    font-size: clamp(12px, 0.729545vw, 16px);
    font-family: 'Poppins', sans-serif;
    font-weight: 500;
    line-height: 19px;
    word-wrap: break-word;
}

.add-button:hover .button-content {
    background-color: #0f172a;
    outline-color: #0f172a;
}
.add-button:hover .button-text {
    color: #fff;
}
.add-button:active .button-content {
    background-color: #f1f3f5;
}

.schedule-card {
    @apply ppv2-bg-white ppv2-rounded-lg ppv2-shadow ppv2-p-4 ppv2-border ppv2-border-gray-200;
}

.status-badge {
    @apply ppv2-px-3 ppv2-py-1 ppv2-rounded-full ppv2-text-sm ppv2-font-medium ppv2-text-white;
}

.establishment-info {
    @apply ppv2-text-sm ppv2-text-gray-600 ppv2-bg-gray-100 ppv2-px-2 ppv2-py-1 ppv2-rounded;
}

.footer-actions {
    @apply ppv2-flex ppv2-justify-end ppv2-space-x-3 ppv2-mt-6 ppv2-pt-4 ppv2-border-t ppv2-border-gray-200;
}

.btn-cancel {
    @apply ppv2-px-4 ppv2-py-2 ppv2-text-sm ppv2-font-medium ppv2-text-gray-700 ppv2-bg-white ppv2-border ppv2-border-gray-300 ppv2-rounded-md ppv2-shadow-sm hover:ppv2-bg-gray-50 focus:ppv2-outline-none focus:ppv2-ring-2 focus:ppv2-ring-offset-2 focus:ppv2-ring-blue-500;
}

.btn-save {
    @apply ppv2-inline-flex ppv2-justify-center ppv2-px-4 ppv2-py-2 ppv2-text-sm ppv2-font-medium ppv2-text-white ppv2-bg-blue-600 ppv2-border ppv2-border-transparent ppv2-rounded-md ppv2-shadow-sm hover:ppv2-bg-blue-700 focus:ppv2-outline-none focus:ppv2-ring-2 focus:ppv2-ring-offset-2 focus:ppv2-ring-blue-500;
}

.empty-state {
    @apply ppv2-bg-white ppv2-rounded-lg ppv2-shadow ppv2-border ppv2-border-gray-200;
}

.dark .ppv2-bg-white {
    background-color: rgba(255, 255, 255, 0.1);
}
</style>
